/**
 * Role-based permissions system for admin functionality
 */

export type UserRole = 'USER' | 'MERCHANT' | 'ADMIN' | 'CUSTOMER_SUPPORT' | 'HOUSEKEEPER' | 'PRODUCT_ADMIN';

export type Permission = 
  // Dashboard permissions
  | 'view_dashboard'
  | 'view_analytics'
  
  // User management permissions
  | 'view_users'
  | 'create_users'
  | 'edit_users'
  | 'delete_users'
  | 'manage_user_roles'
  | 'manage_user_balances'
  | 'manage_user_verification'
  | 'view_user_transactions'
  
  // Transaction permissions
  | 'view_transactions'
  | 'search_transactions'
  | 'export_transactions'
  
  // Referral permissions
  | 'view_referrals'
  | 'create_referrals'
  | 'manage_referrals'
  
  // Support permissions
  | 'view_support'
  | 'manage_support'
  
  // Guest conversation permissions
  | 'view_guest_conversations'
  
  // User vote permissions
  | 'view_votes'
  | 'create_votes'
  | 'manage_votes'
  
  // AI Agent management permissions
  | 'view_agents'
  | 'manage_agents'
  | 'create_agents'
  | 'approve_agents'
  | 'revoke_agents'
  
  // Client management permissions
  | 'view_clients'
  | 'create_clients'
  | 'edit_clients'
  | 'delete_clients'
  
  // Settings permissions
  | 'view_settings'
  | 'edit_settings'
  
  // System admin permissions
  | 'view_system_admin'
  | 'manage_system_config'
  | 'view_system_logs'
  | 'manage_api_keys'
  
  // Currency management permissions
  | 'manage_currencies';

// Define permissions for each role
const rolePermissions: Record<UserRole, Permission[]> = {
  USER: [],
  
  MERCHANT: [],
  
  HOUSEKEEPER: [],
  
  CUSTOMER_SUPPORT: [
    // Dashboard access
    'view_dashboard',
    'view_analytics',
    
    // User management (limited)
    'view_users',
    'edit_users', // Can edit user info but not roles
    'manage_user_balances',
    'manage_user_verification',
    'view_user_transactions',
    
    // Transaction access
    'view_transactions',
    'search_transactions',
    'export_transactions',
    
    // Referral access
    'view_referrals',
    'create_referrals',
    'manage_referrals',
    
    // Support access
    'view_support',
    'manage_support',
    
    // Guest conversation access
    'view_guest_conversations',
    
    // Note: User Votes access removed from CUSTOMER_SUPPORT
    // Note: AI Agent access removed from CUSTOMER_SUPPORT
    
    // Settings (read-only)
    'view_settings',
  ],
  
  PRODUCT_ADMIN: [
    // Dashboard access
    'view_dashboard',
    'view_analytics',
    
    // User management (limited)
    'view_users',
    'edit_users',
    'manage_user_balances',
    'manage_user_verification',
    'view_user_transactions',
    
    // Transaction access
    'view_transactions',
    'search_transactions',
    'export_transactions',
    
    // Referral access
    'view_referrals',
    'create_referrals',
    'manage_referrals',
    
    // Support access
    'view_support',
    'manage_support',
    
    // Guest conversation access
    'view_guest_conversations',
    
    // Full vote access (PRODUCT_ADMIN exclusive)
    'view_votes',
    'create_votes',
    'manage_votes',
    
    // Settings (read-only)
    'view_settings',
  ],
  
  ADMIN: [
    // Full access to everything
    'view_dashboard',
    'view_analytics',
    
    // Complete user management
    'view_users',
    'create_users',
    'edit_users',
    'delete_users',
    'manage_user_roles',
    'manage_user_balances',
    'manage_user_verification',
    'view_user_transactions',
    
    // Full transaction access
    'view_transactions',
    'search_transactions',
    'export_transactions',
    
    // Full referral access
    'view_referrals',
    'create_referrals',
    'manage_referrals',
    
    // Full support access
    'view_support',
    'manage_support',
    
    // Guest conversation access
    'view_guest_conversations',
    
    // Full vote access
    'view_votes',
    'create_votes',
    'manage_votes',
    
    // Full AI Agent management (ADMIN exclusive)
    'view_agents',
    'manage_agents',
    'create_agents',
    'approve_agents',
    'revoke_agents',
    
    // Full client management
    'view_clients',
    'create_clients',
    'edit_clients',
    'delete_clients',
    
    // Full settings access
    'view_settings',
    'edit_settings',
    
    // System admin access
    'view_system_admin',
    'manage_system_config',
    'view_system_logs',
    'manage_api_keys',
    
    // Currency management
    'manage_currencies',
  ],
};

/**
 * Get all permissions for a specific role
 * Defined BEFORE hasPermission to avoid use-before-define error
 */
export const getRolePermissions = (role: UserRole): Permission[] => {
  return rolePermissions[role] || [];
};

/**
 * Check if a user has a specific permission
 */
export const hasPermission = (userRoles: string[], permission: Permission): boolean => {
  if (!userRoles || userRoles.length === 0) {
    return false;
  }

  // Check each role the user has
  for (const role of userRoles) {
    const rolePermissions = getRolePermissions(role as UserRole);
    if (rolePermissions.includes(permission)) {
      return true;
    }
  }

  return false;
};

/**
 * Check if user has admin access (ADMIN, CUSTOMER_SUPPORT, or PRODUCT_ADMIN)
 */
export const hasAdminAccess = (userRoles: string[]): boolean => {
  return userRoles?.includes('ADMIN') || userRoles?.includes('CUSTOMER_SUPPORT') || userRoles?.includes('PRODUCT_ADMIN');
};

/**
 * Check if user is full admin (ADMIN role only)
 */
export const isFullAdmin = (userRoles: string[]): boolean => {
  return userRoles?.includes('ADMIN');
};

/**
 * Check if user is customer support
 */
export const isCustomerSupport = (userRoles: string[]): boolean => {
  return userRoles?.includes('CUSTOMER_SUPPORT') && !userRoles?.includes('ADMIN');
};

/**
 * Get user's admin role type
 */
export const getAdminRoleType = (userRoles: string[]): 'admin' | 'customer_support' | 'product_admin' | null => {
  if (userRoles?.includes('ADMIN')) {
    return 'admin';
  }
  if (userRoles?.includes('CUSTOMER_SUPPORT')) {
    return 'customer_support';
  }
  if (userRoles?.includes('PRODUCT_ADMIN')) {
    return 'product_admin';
  }
  return null;
};

/**
 * Filter admin menu items based on user permissions
 */
export interface AdminMenuItem {
  key: string;
  icon: any;
  label: string;
  path: string;
  requiredPermission?: Permission;
}

export const filterAdminMenuItems = (items: AdminMenuItem[], userRoles: string[]): AdminMenuItem[] => {
  return items.filter(item => {
    if (!item.requiredPermission) {
      return true; // No permission required
    }
    return hasPermission(userRoles, item.requiredPermission);
  });
}; 